// =============================================================================
// notesToHankMessageUtils.ts — Builder and parser functions for NTH messages
// =============================================================================

import type {
  NoteMessage,
  PhotoMetadata,
  ConversationContextEntry,
  AttachmentItem,
} from './NotesToHankTypes';

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

export function buildInitialMessage(
  dealId: string,
  text: string,
  photos: PhotoMetadata[],
  conversationContext?: ConversationContextEntry[],
  taskListId?: string
): NoteMessage {
  const msg: NoteMessage = {
    type: 'note_message',
    event_type: 'initial',
    deal_id: dealId,
    text,
    photos,
    timestamp: Date.now(),
  };

  if (taskListId) {
    msg.task_list_id = taskListId;
  }

  if (conversationContext && conversationContext.length > 0) {
    msg.conversation_context = conversationContext;
  }

  return msg;
}

export function buildFollowUpMessage(
  dealId: string,
  text: string,
  photos: PhotoMetadata[],
  selectedAnswerIds: string[],
  pressedActionButtonIds: string[],
  selectedAnswersMap?: Record<string, string>,
  questionPhotoAttachments?: Record<string, string[]>
): NoteMessage {
  const msg: NoteMessage = {
    type: 'note_message',
    event_type: 'follow_up',
    deal_id: dealId,
    text,
    photos,
    selected_answer_ids: selectedAnswerIds,
    pressed_action_button_ids: pressedActionButtonIds,
    timestamp: Date.now(),
  };

  if (selectedAnswersMap && Object.keys(selectedAnswersMap).length > 0) {
    msg.selected_answers_map = selectedAnswersMap;
  }

  if (
    questionPhotoAttachments &&
    Object.keys(questionPhotoAttachments).length > 0
  ) {
    msg.question_photo_attachments = questionPhotoAttachments;
  }

  return msg;
}

// -----------------------------------------------------------------------------
// Photo Attachment Payload Builder
// -----------------------------------------------------------------------------

/**
 * Derives the `question_photo_attachments` payload from the existing
 * `questionAttachments` state. Filters to only completed uploads with a
 * truthy `s3Key`, maps to S3 keys only (not full CDN URLs).
 * Returns `undefined` when no photos qualify (omit field from payload).
 */
export function buildQuestionPhotoAttachmentsPayload(
  questionAttachments: Record<string, AttachmentItem[]>
): Record<string, string[]> | undefined {
  const mapping: Record<string, string[]> = {};
  for (const [questionId, attachments] of Object.entries(questionAttachments)) {
    const s3Keys = attachments
      .filter((att) => att.uploadStatus === 'complete' && att.s3Key)
      .map((att) => att.s3Key!);
    if (s3Keys.length > 0) {
      mapping[questionId] = s3Keys;
    }
  }
  return Object.keys(mapping).length > 0 ? mapping : undefined;
}

// -----------------------------------------------------------------------------
// Parser
// -----------------------------------------------------------------------------

export function parseInboundMessage(raw: string): NoteMessage | null {
  try {
    const parsed = JSON.parse(raw);

    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return null;
    }

    if (parsed.type !== 'note_message') {
      return null;
    }

    const validEventTypes = [
      'initial',
      'follow_up',
      'response',
      'error',
      'thinking',
    ];
    if (!validEventTypes.includes(parsed.event_type)) {
      return null;
    }

    if (typeof parsed.deal_id !== 'string') {
      return null;
    }

    return parsed as NoteMessage;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// URI helpers
// -----------------------------------------------------------------------------

const CLOUDFRONT_BASE = 'https://d22xeeaijfewl2.cloudfront.net/public/';

/**
 * Resolves a photo URI to a full CDN URL.
 * If the URI already starts with `http`, it is returned unchanged.
 * Otherwise, the CloudFront base URL is prepended.
 */
export function resolvePhotoUri(uri: string): string {
  if (uri.startsWith('http')) return uri;
  return `${CLOUDFRONT_BASE}${uri}`;
}

// -----------------------------------------------------------------------------
// UI helpers
// -----------------------------------------------------------------------------

export function isSendEnabled(
  text: string,
  selectedAnswerIds: string[],
  pressedActionButtonIds: string[]
): boolean {
  return (
    text.trim().length > 0 ||
    selectedAnswerIds.length > 0 ||
    pressedActionButtonIds.length > 0
  );
}
